/**
 * Briefing crew als eigen document (Joris 18 sep 2026): achtergrond voor cameraman/regisseur,
 * los van het callsheet dat ook de klant ziet. Bron: callsheet_extra.briefing (markdown).
 * Standaard de crew-weergave (zonder bronverwijzingen); de PL kan bronnen aanzetten.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Stack, CircularProgress, Chip, Alert, Switch, FormControlLabel } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { stripBronRefs, bronRefsNaarLinks, mdLinkComponents } from '../../utils/briefingBronnen';
import CallsheetTekstEditor from './CallsheetTekstEditor';
import { metRegeleinden } from './callsheetMarkdown';

export default function CallSheetBriefing() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [briefing, setBriefing] = useState('');
  const [bronnen, setBronnen] = useState([]);
  const [bronnenTonen, setBronnenTonen] = useState(false);
  const [bewerk, setBewerk] = useState(false);
  const [concept, setConcept] = useState('');
  const [bezig, setBezig] = useState(false);
  const [pdfBezig, setPdfBezig] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [d, e] = await Promise.all([
          authFetch(`${BACKEND_URL}/Api/callsheets/${id}`).then((r) => r.json()),
          authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`).then((r) => r.json()),
        ]);
        setDetail(d); setBriefing(e?.briefing || ''); setConcept(e?.briefing || ''); setBronnen(e?.briefingBronnen || []);
      } catch (err) { setError(err.message || 'Laden mislukt'); }
    })();
  }, [id]);

  const bewaar = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/extra`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ briefing: concept }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setBriefing(concept); setBewerk(false); toast.success('Briefing opgeslagen');
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); }
    finally { setBezig(false); }
  };

  const f = detail?.callsheet?.fields || {};
  const downloadPdf = async () => {
    setPdfBezig(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/callsheets/${id}/briefing.pdf`);
      if (!r.ok) throw new Error(`PDF maken mislukt (HTTP ${r.status})`);
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a'); a.href = url; a.download = `Briefing ${String(f.Titel || 'draaidag').replace(/[\\/:*?"<>|]+/g, ' ').trim()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
    finally { setPdfBezig(false); }
  };

  const crew = (detail?.crew || []).map((c) => `${c.fields?.['Volledige naam (from Personen)']?.[0] || c.fields?.Persoon || ''}${c.fields?.['Functie (handmatig)'] ? ` (${c.fields['Functie (handmatig)']})` : ''}`).filter(Boolean);
  const loc = (detail?.locaties || [])[0]?.fields || {};
  const weergave = bronnenTonen ? bronRefsNaarLinks(briefing, bronnen) : stripBronRefs(briefing);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 1.5, sm: 2.5 }, py: 2, '@media print': { maxWidth: '100%', px: 0 } }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2, '@media print': { display: 'none' } }}>
        <Button size="small" onClick={() => navigate(`/callsheets/${id}`)} sx={{ textTransform: 'none' }}>← Callsheet</Button>
        <Button size="small" onClick={() => navigate('/callsheets')} sx={{ textTransform: 'none' }}>Overzicht draaidagen</Button>
        <Box sx={{ flex: 1 }} />
        {!bewerk && briefing && (
          <FormControlLabel sx={{ mr: 1 }} control={<Switch size="small" checked={bronnenTonen} onChange={(e) => setBronnenTonen(e.target.checked)} />}
            label={<Typography variant="caption" color="text.secondary">Bronnen tonen (PL)</Typography>} />
        )}
        {!bewerk && <Button size="small" variant="outlined" onClick={() => { setConcept(briefing); setBewerk(true); }} sx={{ textTransform: 'none' }}>Bewerken</Button>}
        {!bewerk && briefing && <Button size="small" variant="contained" disableElevation disabled={pdfBezig} onClick={downloadPdf} sx={{ textTransform: 'none', fontWeight: 700 }}>{pdfBezig ? 'PDF maken…' : 'PDF downloaden'}</Button>}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!detail ? <CircularProgress size={22} /> : (
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 2, '@media print': { border: 0 } }}>
          <Chip size="small" label="Briefing crew · intern, niet voor de klant" sx={{ mb: 1.5, bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 700 }} />
          <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>{f.Titel || 'Callsheet'}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {[f['Datum (handmatig)'] || f.Datum, [loc['Naam locatie'], loc.Plaats].filter(Boolean).join(', '), crew.length ? `Crew: ${crew.join(', ')}` : null].filter(Boolean).join(' · ')}
          </Typography>
          {bewerk ? (
            <>
              {/* Opmaak-editor i.p.v. ruwe markdown (wiki-feedback Callsheets 26 sep). */}
              <CallsheetTekstEditor value={concept} onChange={setConcept} minHeight={360} autoFocus />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                <Button size="small" onClick={() => setBewerk(false)} sx={{ textTransform: 'none' }}>Annuleren</Button>
                <Button size="small" variant="contained" disableElevation disabled={bezig} onClick={bewaar} sx={{ textTransform: 'none', fontWeight: 700 }}>{bezig ? 'Opslaan…' : 'Opslaan'}</Button>
              </Stack>
            </>
          ) : briefing ? (
            <Box sx={{ '& h1, & h2, & h3': { fontSize: '1.05rem', fontWeight: 800, mt: 2.5, mb: 0.75 }, '& ul': { pl: 2.5, my: 0.5 }, '& li': { mb: 0.4, lineHeight: 1.55 }, '& p': { lineHeight: 1.6 }, '& a': { color: '#4338ca', wordBreak: 'break-all' } }}>
              <ReactMarkdown components={mdLinkComponents}>{metRegeleinden(weergave)}</ReactMarkdown>
            </Box>
          ) : (
            <Typography color="text.secondary">Nog geen briefing. Maak hem op het callsheet met “Voorbereiden met AI”, of klik op Bewerken.</Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
